import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { ArrowRight } from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { StructuredData } from "@/components/StructuredData";
import { serviceStates } from "@/data/serviceAreas";
import { assets, siteConfig } from "@/data/site";

const installationScopeStatement = "Final installation scope, availability, schedule, access and site conditions are confirmed before work proceeds.";
const installationReviewQualifier = `Supply and installation requests in these states are reviewed per project. ${installationScopeStatement}`;

export default function ServiceAreas() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: `${siteConfig.url}/service-areas` },
    ],
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${siteConfig.url}/service-areas#collection`,
        name: "Nationwide Bathroom Partition Shipping and Full-Service States",
        url: `${siteConfig.url}/service-areas`,
        description: `Nationwide commercial bathroom partition and restroom accessory material shipping, with supply and installation requests reviewed per project in seven states. ${installationScopeStatement}`,
        about: [
          { "@id": `${siteConfig.url}/#business` },
          { "@id": `${siteConfig.url}/service-areas#shipping-service` },
          ...serviceStates.map((state) => ({ "@id": `${siteConfig.url}/service-areas/${state.slug}#service` })),
        ],
      },
      {
        "@type": "Service",
        "@id": `${siteConfig.url}/service-areas#shipping-service`,
        name: "Nationwide commercial bathroom partition material shipping",
        description: "Commercial bathroom partition materials and restroom accessories delivered across the United States.",
        url: `${siteConfig.url}/service-areas`,
        areaServed: { "@type": "Country", name: "United States" },
        provider: { "@id": `${siteConfig.url}/#business` },
      },
      ...serviceStates.map((state) => ({
        "@type": "Service",
        "@id": `${siteConfig.url}/service-areas/${state.slug}#service`,
        name: `Commercial bathroom partition supply and installation review in ${state.name}`,
        description: `Supply and installation requests in ${state.name} are reviewed per project. ${installationScopeStatement}`,
        url: `${siteConfig.url}/service-areas/${state.slug}`,
        areaServed: { "@type": "State", name: state.name },
        provider: { "@id": `${siteConfig.url}/#business` },
      })),
    ],
  };

  return (
    <>
      <StructuredData data={[breadcrumbSchema, serviceSchema]} />
      <header className="service-hero section-line">
        <div className="service-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span> Service areas</p>
          <span className="kicker">01 / Nationwide material shipping</span>
          <h1>Bathroom partitions shipped nationwide.</h1>
          <p>Partition Stalls ships commercial bathroom partitions and restroom accessories across the United States. Full-service supply + installation states begin directly below.</p>
          <div className="button-row"><Link href="/request-a-quote" className="button button--primary">Request a Quote <ArrowUpRight aria-hidden="true" /></Link><a href={`tel:${siteConfig.phoneHref}`} className="button button--outline">Call {siteConfig.phoneDisplay}</a></div>
        </div>
        <figure className="service-hero__art">
          <img src={assets.nationwideShippingArtworkMedium} srcSet={`${assets.nationwideShippingArtworkSmall} 720w, ${assets.nationwideShippingArtworkMedium} 1280w, ${assets.nationwideShippingArtworkLarge} 1800w`} sizes="(max-width: 780px) 100vw, 58vw" width="1800" height="1440" alt="Continental United States map with a Partition Stalls delivery van, nationwide shipping routes, and miniature commercial bathroom partition stalls" fetchPriority="high" />
          <figcaption><span>Nationwide material shipping</span><strong>Commercial bathroom partition materials and restroom accessories delivered across the United States.</strong></figcaption>
        </figure>
      </header>

      <section className="full-service-states section-line" aria-labelledby="state-card-title">
        <div className="full-service-states__intro">
          <div><span className="kicker kicker--light">02 / Full-service states</span><h2 id="state-card-title">Supply + installation in seven states.</h2><p>{installationReviewQualifier}</p></div>
            <p>Select a state for full-service bathroom partition supply + installation, priority markets, planning guidance, and a direct quote.</p>
        </div>
        <div className="state-card-grid">
          {serviceStates.map((state) => (
            <article id={state.slug} className="state-card" key={state.abbreviation}>
              <Link href={`/service-areas/${state.slug}`} className="state-card__link" aria-label={`${state.name} commercial bathroom partition supply and installation; open full-service state page`} aria-describedby={`${state.slug}-service-preview`}>
                <div className="state-card__top"><img src={state.icon} alt="" aria-hidden="true" width="96" height="96" /><span className="state-card__flag"><small>Full service</small><strong>{state.abbreviation}</strong></span></div>
                <h3>{state.name}</h3>
                <p>{state.serviceMode}</p>
                <ul aria-label={`Representative ${state.name} markets`}>{state.markets.map((market) => <li key={market}>{market}</li>)}</ul>
                <span className="state-card__cta">View {state.name} supply + installation <ArrowRight aria-hidden="true" /></span>
                <span id={`${state.slug}-service-preview`} className="state-card__tooltip" role="tooltip">
                  <small>Full-service preview</small>
                  <strong>{state.name}</strong>
                  <span>Commercial bathroom partition supply + installation</span>
                  <span>{state.markets.join(" · ")}</span>
                  <b>Open {state.name} page <ArrowRight aria-hidden="true" /></b>
                </span>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="service-contact-bar section-line" aria-labelledby="service-contact-title"><div><span className="kicker kicker--light">Confirm your project location</span><h2 id="service-contact-title">Tell us where the job is.</h2></div><div className="service-contact-bar__links"><a href={`tel:${siteConfig.phoneHref}`}><Phone aria-hidden="true" /><span>Call</span><strong>{siteConfig.phoneDisplay}</strong></a><a href={`mailto:${siteConfig.email}`}><Mail aria-hidden="true" /><span>Email</span><strong>{siteConfig.email}</strong></a><Link href="/request-a-quote" className="button button--light">Start a Quote <ArrowUpRight aria-hidden="true" /></Link></div></section>
    </>
  );
}
